import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../__generated__/schema-lite';

/** Sends each role to its own half of the app — in ONE place.
 *
 *  This used to be two guards, one in each shell: the customer shell redirected a
 *  platform admin to /admin, and the admin shell redirected everyone else to /. Two
 *  components each able to redirect at the other is a loop waiting to happen — all it
 *  takes is for `me` to be momentarily absent in one and present in the other, or for
 *  one to remount, and the app ping-pongs between the two URLs. That is exactly what
 *  happened in production.
 *
 *  One gate cannot ping-pong: it looks at a single pathname and the two conditions
 *  below are mutually exclusive, so at most one redirect can ever fire.
 *
 *  It renders nothing until `me` resolves. Deciding on an unknown role is the other
 *  half of the old bug — a redirect issued while the answer is still loading gets
 *  reconsidered the moment it arrives. */
export function RoleGate() {
  const { pathname } = useLocation();
  const crud = useClientQueries(schema);
  const { data: me } = crud.$procs.me.useQuery();

  // No decision without an answer.
  if (!me) return null;

  const isPlatformAdmin = (me as { role?: string }).role === 'PLATFORM_ADMIN';
  const inAdminSection = pathname === '/admin' || pathname.startsWith('/admin/');

  if (isPlatformAdmin && !inAdminSection) return <Navigate to="/admin" replace />;
  if (!isPlatformAdmin && inAdminSection) return <Navigate to="/" replace />;

  return <Outlet />;
}
